import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, Form, Spinner } from 'react-bootstrap';
import { GET_SUBSCRIPTION_FEATURE_TOGGLES, UPDATE_SUBSCRIPTION_FEATURE_TOGGLES } from './graphql';

type SubscriptionFeatureToggleValues = {
  autoRenewReminderPopupEnabled: boolean;
  sandboxSubscriptionSkuCheatEnabled: boolean;
  sandboxSubscriptionSkuCheatUserIds: string[];
  economicTransactionReceiptLogEnabled: boolean;
  unblockFriendEnabled: boolean;
};

type SubscriptionFeatureTogglesQuery = {
  app: {
    id: string;
  } & SubscriptionFeatureToggleValues;
};

type UpdateSubscriptionFeatureTogglesMutation = {
  updateAppFeatureToggles: SubscriptionFeatureToggleValues;
};

type UpdateSubscriptionFeatureTogglesVariables = Partial<SubscriptionFeatureToggleValues>;

const SubscriptionFeatureToggles = () => {
  const { data, loading, error } = useQuery<SubscriptionFeatureTogglesQuery>(GET_SUBSCRIPTION_FEATURE_TOGGLES, {
    fetchPolicy: 'network-only',
  });
  const [updateToggles, { loading: updateLoading }] = useMutation<
    UpdateSubscriptionFeatureTogglesMutation,
    UpdateSubscriptionFeatureTogglesVariables
  >(UPDATE_SUBSCRIPTION_FEATURE_TOGGLES, {
    refetchQueries: [{ query: GET_SUBSCRIPTION_FEATURE_TOGGLES }],
  });

  const toggles = data?.app;
  const isSaving = updateLoading;
  const [autoRenewReminderPopupEnabled, setAutoRenewReminderPopupEnabled] = useState(false);
  const [sandboxSubscriptionSkuCheatEnabled, setSandboxSubscriptionSkuCheatEnabled] = useState(false);
  const [economicTransactionReceiptLogEnabled, setEconomicTransactionReceiptLogEnabled] = useState(true);
  const [unblockFriendEnabled, setUnblockFriendEnabled] = useState(false);
  const [sandboxUserIdsText, setSandboxUserIdsText] = useState('');
  const [showSandboxUserIdsError, setShowSandboxUserIdsError] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const sandboxUserIds = useMemo(
    () =>
      sandboxUserIdsText
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    [sandboxUserIdsText]
  );

  useEffect(() => {
    setAutoRenewReminderPopupEnabled(!!toggles?.autoRenewReminderPopupEnabled);
    setSandboxSubscriptionSkuCheatEnabled(!!toggles?.sandboxSubscriptionSkuCheatEnabled);
    setEconomicTransactionReceiptLogEnabled(toggles?.economicTransactionReceiptLogEnabled ?? true);
    setUnblockFriendEnabled(!!toggles?.unblockFriendEnabled);
    setSandboxUserIdsText(toggles?.sandboxSubscriptionSkuCheatUserIds.join('\n') ?? '');
  }, [toggles]);

  const handleSave = () => {
    if (sandboxSubscriptionSkuCheatEnabled && sandboxUserIds.length === 0) {
      setShowSandboxUserIdsError(true);
      return;
    }

    setShowSandboxUserIdsError(false);
    updateToggles({
      variables: {
        autoRenewReminderPopupEnabled,
        sandboxSubscriptionSkuCheatEnabled,
        sandboxSubscriptionSkuCheatUserIds: sandboxUserIds,
        economicTransactionReceiptLogEnabled,
        unblockFriendEnabled,
      },
    })
      .then(() => {
        setShowSaveSuccess(true);
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  return (
    <div className="d-flex flex-column">
      <h1>Feature Toggles</h1>
      <p className="text-muted">
        Backend-managed switches, grouped by what they affect. Changes apply to this environment only and take
        effect within a minute, no deploy needed.
      </p>

      {error ? <Alert variant="danger">{error.message}</Alert> : null}
      {showSaveSuccess ? <Alert variant="success">successfully saved</Alert> : null}

      <Card className="mb-3">
        <Card.Body>
          {loading && !toggles ? (
            <Spinner animation="border" />
          ) : (
            <>
              <h5 className="mb-1">Subscriptions</h5>
              <p className="text-muted small mb-3">Client behaviour for every player.</p>
              <Form.Group controlId="autoRenewReminderPopupEnabled">
                <Form.Check
                  type="switch"
                  label="Auto renewal subscription popup"
                  checked={autoRenewReminderPopupEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setAutoRenewReminderPopupEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                  }}
                />
                <Form.Text className="text-muted">
                  Shows the renewal reminder popup.
                </Form.Text>
              </Form.Group>

              <hr />
              <h5 className="mb-1">Social</h5>
              <p className="text-muted small mb-3">Client behaviour for every player.</p>
              <Form.Group controlId="unblockFriendEnabled">
                <Form.Check
                  type="switch"
                  label="Unblock players FE"
                  checked={unblockFriendEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setUnblockFriendEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                  }}
                />
                <Form.Text className="text-muted">
                  ON: the Friends screen has Friends / Blocked tabs, players can unblock from the Blocked tab, and
                  blocking someone opens the Blocked tab. OFF: the old Friends screen without tabs, blocking goes back
                  to the main menu, and the backend rejects player unblocks (admin Unblock still works). Default: OFF.
                  Players see the change the next time they open the Friends screen.
                </Form.Text>
              </Form.Group>

              <hr />
              <h5 className="mb-1">QA / Sandbox</h5>
              <p className="text-muted small mb-3">Client test tooling, visible only to the allowed user ids below.</p>
              <Form.Group controlId="sandboxSubscriptionSkuCheat" className="mb-0">
                <Form.Check
                  type="switch"
                  label="Sandbox subscription SKU buttons"
                  checked={sandboxSubscriptionSkuCheatEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setSandboxSubscriptionSkuCheatEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                    if (!event.currentTarget.checked) {
                      setShowSandboxUserIdsError(false);
                    }
                  }}
                />
                <Form.Text className="text-muted">
                  Shows SKU test buttons for allowed users.
                </Form.Text>
              </Form.Group>

              <Form.Group controlId="sandboxSubscriptionSkuCheatUserIds" className="mt-3 mb-0">
                <Form.Label>Allowed user ids</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  isInvalid={showSandboxUserIdsError}
                  value={sandboxUserIdsText}
                  disabled={isSaving}
                  onChange={(event) => {
                    setSandboxUserIdsText(event.currentTarget.value);
                    setShowSaveSuccess(false);
                    if (showSandboxUserIdsError) {
                      setShowSandboxUserIdsError(false);
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  Add at least one user id.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Separate ids with commas, spaces, or new lines.
                </Form.Text>
              </Form.Group>

              <hr />
              <h5 className="mb-1">Server logging</h5>
              <p className="text-muted small mb-3">
                Backend only, no effect on players. Default differs per environment: ON on test, OFF on beta and prod.
              </p>
              <Form.Group controlId="economicTransactionReceiptLogEnabled">
                <Form.Check
                  type="switch"
                  label="Server receipt logs for store purchases"
                  checked={economicTransactionReceiptLogEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setEconomicTransactionReceiptLogEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                  }}
                />
                <Form.Text className="text-muted">
                  Writes a <code>[economic_transaction] store purchase receipt</code> line (GRANTED / DUPLICATE_SKIPPED,
                  with userId) to Cloud Logging for every store purchase, to reconcile against the client&apos;s
                  economic_transaction events. Default: ON on test, OFF on beta and prod. Saving here overrides the
                  default for this environment. Applies within a minute.
                </Form.Text>
              </Form.Group>

              <button type="button" className="btn btn-primary mt-3" disabled={isSaving} onClick={handleSave}>
                Save
              </button>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SubscriptionFeatureToggles;
